import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'zh-TW', label: '繁體中文', flag: '🇭🇰' },
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'th', label: 'ไทย', flag: '🇹🇭' },
  { code: 'id', label: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(LANGUAGES[0]);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      if (menuOpen) setMenuOpen(false);
      if (langOpen) setLangOpen(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [menuOpen, langOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMenuOpen(false);
    }
  };

  const handleLangChange = (lang: typeof LANGUAGES[0]) => {
    setCurrentLang(lang);
    setLangOpen(false);
    i18n.changeLanguage(lang.code);
  };

  const navLinks = [
    { id: 'core-logic', labelKey: 'nav.coreLogic' },
    { id: 'profit-mechanism', labelKey: 'nav.profitMechanism' },
    { id: 'flywheel', labelKey: 'nav.profitFlywheel' },
    { id: 'system-architecture', labelKey: 'nav.sysArch' },
    { id: 'trading-example', labelKey: 'nav.tradingExample' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
      scrolled || menuOpen
        ? 'bg-[#0c0814]/92 backdrop-blur-2xl border-b border-purple-500/10'
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div
            className="flex items-center gap-2.5 cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img
              src="https://public.readdy.ai/ai/img_res/68826112-cb48-47f9-a325-24ef38e58275.png"
              alt="PolyWin"
              className="w-8 h-8 md:w-9 md:h-9 object-contain"
            />
            <span className="text-xl md:text-2xl font-bold text-white font-heading tracking-tight">
              Poly<span className="text-purple-400">Win</span>
            </span>
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-sm font-medium text-white/50 hover:text-purple-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t(link.labelKey)}
              </button>
            ))}
          </div>

          {/* CTA + Language + Hamburger */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(v => !v)}
                className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:bg-white/8 transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="text-base">{currentLang.flag}</span>
                <span>{currentLang.label}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Dropdown */}
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 rounded-2xl overflow-hidden shadow-2xl border border-white/10"
                  style={{ background: 'rgba(18,12,34,0.98)', backdropFilter: 'blur(20px)' }}>
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangChange(lang)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all cursor-pointer ${
                        currentLang.code === lang.code
                          ? 'bg-purple-500/15 text-purple-300'
                          : 'text-white/70 hover:bg-white/6 hover:text-white'
                      }`}
                    >
                      <span className="text-base w-6 text-center">{lang.flag}</span>
                      <span className="flex-1 text-left">{lang.label}</span>
                      {currentLang.code === lang.code && (
                        <i className="ri-check-line text-purple-400 text-sm"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a href="https://www.polywin.ai/" target="_blank" rel="nofollow noopener noreferrer" className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer text-white"
              style={{ background: 'linear-gradient(135deg, #9333ea, #7c3aed)', boxShadow: '0 4px 20px rgba(147,51,234,0.4)' }}>
              {t('nav.cta')}
              <i className="ri-arrow-right-line"></i>
            </a>
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl text-white/50 hover:text-purple-400 transition-colors cursor-pointer"
              onClick={() => setMenuOpen(v => !v)}
              aria-label="menu"
            >
              <i className={`text-2xl transition-all duration-300 ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden overflow-hidden transition-all duration-300 ${menuOpen ? 'max-h-[520px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-[#100b1e]/98 backdrop-blur-xl border-t border-purple-500/10 px-4 pt-2 pb-4 space-y-1">
          {navLinks.map(link => (
            <button
              key={link.id}
              onClick={() => scrollToSection(link.id)}
              className="w-full text-left px-4 py-3 rounded-xl text-white/60 font-medium hover:bg-purple-400/10 hover:text-purple-300 transition-colors cursor-pointer text-sm"
            >
              {t(link.labelKey)}
            </button>
          ))}

          {/* Mobile Language Grid */}
          <div className="pt-3 pb-1">
            <p className="text-xs text-white/30 px-4 mb-2">{t('nav.language')}</p>
            <div className="grid grid-cols-3 gap-1.5">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => handleLangChange(lang)}
                  className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl text-xs transition-all cursor-pointer ${
                    currentLang.code === lang.code
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                      : 'text-white/50 hover:bg-white/6 hover:text-white/80'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="leading-tight text-center">{lang.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2">
            <button className="w-full px-6 py-3 rounded-full text-sm font-bold transition-all cursor-pointer text-white"
              style={{ background: 'linear-gradient(135deg,#9333ea,#7c3aed)' }}>
              {t('nav.cta')}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
